import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from '../utils/toast';
import { PlayCircle, FileText } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PreAssessmentNotesViewPage = () => {
  const navigate = useNavigate();
  const { topicId } = useParams();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/signin');
      return;
    }

    fetchNotes();
  }, [navigate]);

  const fetchNotes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assessments/pre-assessment-notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setContent(response.data.content || '');
    } catch (error) {
      console.error('Error fetching pre-assessment notes:', error);
      toast.error('Failed to load pre-assessment notes');
      // Use default content on error
      setContent(`Complete the assessment in a single attempt.

If you exit midway, your progress will not be saved and the assessment will restart from the beginning.

Ensure a stable internet connection to avoid interruptions.

Avoid switching tabs or minimizing the screen during the assessment, as it may reset your attempt.

Read each question carefully before submitting your answers.`);
    } finally {
      setLoading(false);
    }
  };

  const handleStartAssessment = () => {
    // Navigate to the topic with assessment mode
    if (topicId) {
      navigate(`/staff/dashboard?openTopic=${topicId}&startAssessment=true`);
    } else {
      navigate('/staff/dashboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-3 md:p-6">
      <Card className="max-w-3xl w-full shadow-2xl">
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="text-center mb-6 md:mb-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <FileText className="w-8 h-8 text-[#0056B3]" />
              </div>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Pre-Assessment Notes
            </h1>
            <p className="text-sm md:text-base text-gray-600">
              Please read the following guidelines carefully before starting the assessment
            </p>
          </div>

          {/* Content */}
          <div className="bg-gray-50 rounded-lg p-4 md:p-6 mb-6 md:mb-8">
            <div className="space-y-3">
              {content.split('\n').map((line, index) => (
                line.trim() ? (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#0056B3] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                      {line.trim()}
                    </p>
                  </div>
                ) : (
                  <div key={index} className="h-2"></div>
                )
              ))}
            </div>
          </div>

          {/* Important Notice */}
          <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4 mb-6 md:mb-8">
            <div className="flex items-start gap-3">
              <div className="text-amber-600 font-bold text-lg flex-shrink-0">⚠️</div>
              <p className="text-sm text-amber-800">
                <span className="font-semibold">Important:</span> A minimum score of 80% is required to pass the assessment.
                If you score below 80%, you will need to retake the assessment.
              </p>
            </div>
          </div>

          {/* Start Button */}
          <div className="flex justify-center">
            <Button
              onClick={handleStartAssessment}
              className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white px-8 py-6 text-base md:text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all w-full md:w-auto flex items-center justify-center gap-3"
            >
              <PlayCircle className="w-6 h-6" />
              <span>Start Assessment</span>
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PreAssessmentNotesViewPage;
